import React, { useState, useCallback } from 'react';
import BreathingPlayer from './players/BreathingPlayer';
import MeditationPlayer from './players/MeditationPlayer';
import RelaxationPlayer from './players/RelaxationPlayer';
import MovementPlayer from './players/MovementPlayer';
import SimpleInstructionPlayer from './players/SimpleInstructionPlayer';
import TrackingInputPlayer from './players/TrackingInputPlayer';

const TaskPlayerModal = ({ task, onComplete, onClose }) => {
  const [showCompletion, setShowCompletion] = useState(false);
  const [executionData, setExecutionData] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [userNote, setUserNote] = useState('');
  const [followupAnswer, setFollowupAnswer] = useState('');

  const taskType = task?.task_detail?.task_type || 'simple_instruction';
  const playerConfig = task?.task_detail?.player_config || {};
  const taskTitle = task?.task_detail?.title || 'Görev';
  const catColor = task?.task_detail?.category_color || '#22D3EE';

  const handlePlayerComplete = useCallback((execData) => {
    setExecutionData(execData);
    setShowCompletion(true);
  }, []);

  const handleFinalSubmit = (status) => {
    onComplete({
      status,
      user_note: userNote || null,
      followup_answer: followupAnswer || null,
      execution_data: executionData,
    });
  };

  const statusOptions = [
    { status: 'completed', label: 'Tamamladım', color: '#34D399', bg: 'rgba(52,211,153,0.1)' },
    { status: 'partial', label: 'Kısmen Yaptım', color: '#FBBF24', bg: 'rgba(251,191,36,0.1)' },
    { status: 'skipped', label: 'Yapamadım', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  ];

  const renderPlayer = () => {
    const commonProps = { config: playerConfig, taskTitle, onComplete: handlePlayerComplete, onClose };
    switch (taskType) {
      case 'interactive_breathing': return <BreathingPlayer {...commonProps} />;
      case 'guided_meditation': return <MeditationPlayer {...commonProps} />;
      case 'guided_relaxation': return <RelaxationPlayer {...commonProps} />;
      case 'guided_movement': return <MovementPlayer {...commonProps} />;
      case 'tracking_input': return <TrackingInputPlayer {...commonProps} />;
      default: return <SimpleInstructionPlayer {...commonProps} instructions={task?.task_detail?.instructions} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="task-player-modal">
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, rgba(8,12,20,0.97), rgba(4,6,12,0.99))',
        backdropFilter: 'blur(40px)',
      }} />

      <div className="relative w-full h-full flex items-center justify-center overflow-y-auto">
        {!showCompletion ? renderPlayer() : (
          <div className="flex flex-col items-center w-full max-w-md px-6 gap-5 animate-fadeIn" data-testid="task-completion-form-modal">
            <h2 className="text-white text-xl font-bold">Görev Nasıl Geçti?</h2>

            {task?.task_detail?.followup_question && (
              <div className="w-full p-4 rounded-xl" style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.15)' }}>
                <p className="text-purple-300 text-sm font-medium mb-2">{task.task_detail.followup_question}</p>
                <input type="text" value={followupAnswer} onChange={e => setFollowupAnswer(e.target.value)}
                  placeholder="Cevabını yaz..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 outline-none focus:border-purple-400/40 transition"
                  data-testid="modal-followup-input" />
              </div>
            )}

            <div className="w-full grid grid-cols-3 gap-2">
              {statusOptions.map(opt => (
                <button key={opt.status} onClick={() => setSelectedStatus(opt.status)}
                  className="flex flex-col items-center gap-1.5 py-3.5 rounded-xl text-xs font-semibold transition-all hover:scale-[1.02]"
                  style={{
                    background: selectedStatus === opt.status ? opt.bg : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${selectedStatus === opt.status ? opt.color + '50' : 'rgba(255,255,255,0.05)'}`,
                    color: selectedStatus === opt.status ? opt.color : 'rgba(255,255,255,0.4)',
                    boxShadow: selectedStatus === opt.status ? `0 0 15px ${opt.color}20` : 'none',
                  }} data-testid={`modal-status-${opt.status}`}>
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="w-full">
              <p className="text-white/30 text-xs mb-1.5">Not (opsiyonel)</p>
              <textarea value={userNote} onChange={e => setUserNote(e.target.value)}
                placeholder="Nasıl hissettin? Kısa bir not bırak..."
                rows={2}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 outline-none focus:border-cyan-400/40 transition resize-none"
                data-testid="modal-user-note" />
            </div>

            <div className="flex gap-3 w-full">
              <button onClick={() => selectedStatus && handleFinalSubmit(selectedStatus)}
                disabled={!selectedStatus}
                className="flex-1 py-3.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-30"
                style={{
                  background: selectedStatus
                    ? `linear-gradient(135deg, ${statusOptions.find(o => o.status === selectedStatus)?.color || catColor}, ${statusOptions.find(o => o.status === selectedStatus)?.color || catColor}BB)`
                    : 'rgba(255,255,255,0.1)',
                  boxShadow: selectedStatus ? `0 8px 25px ${statusOptions.find(o => o.status === selectedStatus)?.color || catColor}30` : 'none',
                }} data-testid="modal-submit-completion">
                Kaydet
              </button>
              <button onClick={onClose} className="px-4 py-3.5 rounded-xl text-sm text-white/25 hover:text-white/40 transition">
                Kapat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskPlayerModal;
